import { useState, useEffect, useRef, useCallback } from 'react'

import axios from 'axios'
import debounce from 'lodash.debounce'

import { sendExperimentEvent } from 'Utils/Experiments'
import { buildLocalizedPath } from 'Utils/getLocalizedUrl'

function useSearch(isInListingPage = false, searchInputValue = '') {
  const [query, setQuery] = useState(searchInputValue || '')
  const [data, setData] = useState([])
  const [cursorPosition, setCursorPosition] = useState(-1)
  const [areArrowsUsed, setAreArrowsUsed] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const wrapperRef = useRef(null)
  const currentRequest = useRef(null)
  const isRequestPending = useRef(false)
  const requestCounter = useRef(0)

  // Cleanup function to cancel any pending requests
  useEffect(() => {
    return () => {
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }
      if (debouncedChange && debouncedChange.cancel) {
        debouncedChange.cancel()
      }
      isRequestPending.current = false
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        showSuggestions
      ) {
        closeSuggestions()
      }
    }

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSuggestions, closeSuggestions])

  async function handleSuggestions(inputValue) {
    if (isRequestPending.current) {
      return
    }

    isRequestPending.current = true
    requestCounter.current += 1
    const currentRequestId = requestCounter.current

    try {
      // Cancel previous request if it exists
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }

      // Create a new cancel token
      const cancelToken = axios.CancelToken.source()
      currentRequest.current = cancelToken

      const response = await axios.get('/api/internal/items/live_search', {
        params: { query: inputValue },
        cancelToken: cancelToken.token,
        timeout: 3000,
        headers: {
          'Cache-Control': 'no-cache',
        },
      })

      if (currentRequestId === requestCounter.current) {
        onFetchDataSuccess(response)
      }
    } catch (error) {
      if (!axios.isCancel(error) && currentRequestId === requestCounter.current) {
        // eslint-disable-next-line no-console
        console.error('Error fetching search suggestions:', error)
        setData([])
        setShowSuggestions(false)
      }
    } finally {
      isRequestPending.current = false
    }
  }

  function onFetchDataSuccess(response) {
    // temporary hotfix for the new search suggestion list
    const newData = Array.isArray(response.data?.data)
      ? response.data.data
      : response.data || []

    setData(newData)
    sendExperimentEvent('autocomplete_search_suggestion', newData?.autocomplete_search_suggestion)
    setShowSuggestions(true)
  }

  // Create debounced function using useCallback for stability
  const debouncedChange = useCallback(
    debounce(async (value) => {
      if (!value || !value.trim()) {
        setData([])
        setShowSuggestions(false)
        return
      }

      await handleSuggestions(value)
    }, 300),
    []
  )

  function onInputChange(event) {
    const value = event.target.value
    setQuery(value)
    setCursorPosition(-1)
    setAreArrowsUsed(false)

    if (value.length > 0) {
      debouncedChange(value)
    } else {
      debouncedChange.cancel()
      setData([])
      setShowSuggestions(false)
    }
  }

  function handleKeyDown(e) {
    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38) {
      e.preventDefault()
      setAreArrowsUsed(true)
      if (cursorPosition > 0) {
        setCursorPosition(cursorPosition - 1)
      }
    } else if (e.keyCode === 40) {
      e.preventDefault()
      setAreArrowsUsed(true)
      if (cursorPosition < data.length - 1) {
        setCursorPosition(cursorPosition + 1)
      }
    } else if (e.keyCode === 27) {
      setShowSuggestions(false)
      setCursorPosition(-1)
      setAreArrowsUsed(false)
    }
  }

  function getSearchParams() {
    if (isInListingPage) {
      // this logic makes sense for listing page only
      const currentSearch = window.location.search
      const currentParams = new URLSearchParams(currentSearch)

      // if we have keywords, remove them
      if (currentParams.has('keywords')) {
        currentParams.delete('keywords')
      }

      // always jump to first page with new search
      if (currentParams.has('page')) {
        currentParams.delete('page')
      }

      // if we have suggested_keywords, remove them
      if (currentParams.has('suggested_keywords')) {
        currentParams.delete('suggested_keywords')
      }

      // if we have image_id param, from similar image search flow, remove that.
      if (currentParams.has('image_id')) {
        currentParams.delete('image_id')
      }

      if (currentParams.has('disable_fuzzy_matcher')) {
        currentParams.delete('disable_fuzzy_matcher')
      }

      // if we have some params, add them with &
      if (currentParams.size) {
        return `&${currentParams.toString()}`
      }

      return ''
    }

    return ''
  }

  function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)

    // Cancel any pending requests
    if (currentRequest.current) {
      currentRequest.current.cancel()
    }

    // Cancel any pending debounced calls
    if (debouncedChange && debouncedChange.cancel) {
      debouncedChange.cancel()
    }

    if (window.searchTracker) {
      window.searchTracker({
        searchTerm: query,
        type: 'model',
      })
    }

    const hasData = data.length > 0
    const hasSelectedOption = areArrowsUsed && cursorPosition > -1

    if (hasData && hasSelectedOption) {
      const currentKeyword = data[cursorPosition][0]

      if (currentKeyword) {
        return (window.location.href = buildLocalizedPath(
          `/search?keywords=${encodeURIComponent(currentKeyword)}${getSearchParams()}`
        ))
      }
    }

    return (window.location.href = buildLocalizedPath(
      `/search?keywords=${encodeURIComponent(query)}${getSearchParams()}`
    ))
  }

  function closeSuggestions() {
    setShowSuggestions(false)
    setCursorPosition(-1)
    setAreArrowsUsed(false)
  }

  return {
    query,
    wrapperRef,
    showSuggestions,
    suggestions: data,
    onInputChange,
    handleKeyDown,
    submitting,
    handleSubmit,
    cursorPosition,
  }
}

export { useSearch }
